import type { AnchoredRow } from '../types/anchoredRowTypes'

/**
 * Revision metadata kept alongside, but never written onto, an anchored row.
 *
 * A row entry is reusable while the AnchoredRow reference is reusable. The
 * store treats rows as immutable values, so a new reference is the signal that
 * a row's content may have changed.
 */
export interface LedgerSafeProjectedRow {
  readonly id: string
  readonly row: AnchoredRow
  readonly revision: number
}

/**
 * A structurally shared view of the rows currently resolved for one studio
 * scope. Revisions are transient cache hints; they are not persisted data.
 */
export interface LedgerSafeRowProjection {
  readonly scopeKey: string
  readonly rows: readonly AnchoredRow[]
  readonly rowIds: readonly string[]
  readonly entries: readonly LedgerSafeProjectedRow[]
  readonly entryById: ReadonlyMap<string, LedgerSafeProjectedRow>
  readonly rowById: ReadonlyMap<string, AnchoredRow>
  readonly canonicalRowIndexById: ReadonlyMap<string, number>
  readonly orderedStructuralRows: readonly AnchoredRow[]
  readonly structuralRowIds: readonly string[]
  readonly structuralRowOrderById: ReadonlyMap<string, number>
  /**
   * Current rows whose entry was inserted or replaced while producing this
   * projection. This describes the delta that created the projection.
   */
  readonly changedRowIds: ReadonlySet<string>
  /**
   * IDs removed from the preceding projection in the same scope.
   */
  readonly removedRowIds: ReadonlySet<string>
  /**
   * Advances for inserted, removed, or reference-replaced rows.
   */
  readonly contentRevision: number
  /**
   * Advances when canonical membership/order or structural order changes.
   */
  readonly orderRevision: number
}

export interface ReconcileLedgerSafeRowProjectionOptions {
  readonly previous?: LedgerSafeRowProjection | null
  readonly scopeKey: string
  readonly rows: readonly AnchoredRow[]
}

const haveSameIds = (
  left: readonly string[],
  right: readonly string[],
): boolean => (
  left.length === right.length
  && left.every((id, index) => id === right[index])
)

const compareStructuralRows = (
  left: AnchoredRow,
  right: AnchoredRow,
): number => (
  left.position - right.position
  || left.labelNumber - right.labelNumber
  || left.id.localeCompare(right.id)
)

const collectUniqueRowIds = (rows: readonly AnchoredRow[]): Set<string> => {
  const rowIds = new Set<string>()
  rows.forEach(row => {
    if (rowIds.has(row.id)) {
      throw new Error(`Duplicate anchored row id in Ledger Safe row projection: ${row.id}`)
    }
    rowIds.add(row.id)
  })
  return rowIds
}

/**
 * Reconciles a resolved row array without cloning or decorating its rows.
 *
 * The caller must replace an AnchoredRow object when its content changes.
 * Mutating a row in place breaks the identity contract and is intentionally
 * not compensated for with deep serialization in this hot path.
 */
export const reconcileLedgerSafeRowProjection = ({
  previous = null,
  scopeKey,
  rows,
}: ReconcileLedgerSafeRowProjectionOptions): LedgerSafeRowProjection => {
  const currentRowIdSet = collectUniqueRowIds(rows)
  const rowIdsFromInput = rows.map(row => row.id)
  const previousInScope = previous?.scopeKey === scopeKey ? previous : null
  const canonicalOrderUnchanged = Boolean(
    previousInScope
    && haveSameIds(previousInScope.rowIds, rowIdsFromInput),
  )

  if (
    previousInScope
    && canonicalOrderUnchanged
    && rows.every((row, index) => previousInScope.rows[index] === row)
  ) {
    return previousInScope
  }

  const changedRowIds = new Set<string>()
  const removedRowIds = new Set<string>()
  const entries = rows.map(row => {
    const previousEntry = previousInScope?.entryById.get(row.id)
    if (previousEntry?.row === row) {
      return previousEntry
    }

    changedRowIds.add(row.id)
    return {
      id: row.id,
      row,
      revision: (previousEntry?.revision || 0) + 1,
    }
  })

  previousInScope?.rowIds.forEach(rowId => {
    if (!currentRowIdSet.has(rowId)) {
      removedRowIds.add(rowId)
    }
  })

  const contentChanged = (
    changedRowIds.size > 0
    || removedRowIds.size > 0
  )
  const rowIds = canonicalOrderUnchanged
    ? previousInScope!.rowIds
    : rowIdsFromInput
  const rowsSnapshot = rows.slice()

  const structuralRowsFromInput = [...rows].sort(compareStructuralRows)
  const structuralRowIdsFromInput = structuralRowsFromInput.map(row => row.id)
  const structuralOrderUnchanged = Boolean(
    previousInScope
    && haveSameIds(previousInScope.structuralRowIds, structuralRowIdsFromInput),
  )
  const orderChanged = (
    !canonicalOrderUnchanged
    || !structuralOrderUnchanged
  )

  const entryById = previousInScope && !contentChanged
    ? previousInScope.entryById
    : new Map(entries.map(entry => [entry.id, entry]))
  const rowById = previousInScope && !contentChanged
    ? previousInScope.rowById
    : new Map(rowsSnapshot.map(row => [row.id, row]))
  const canonicalRowIndexById = canonicalOrderUnchanged
    ? previousInScope!.canonicalRowIndexById
    : new Map(rowIds.map((rowId, index) => [rowId, index]))
  const structuralRowIds = structuralOrderUnchanged
    ? previousInScope!.structuralRowIds
    : structuralRowIdsFromInput
  const structuralRowOrderById = structuralOrderUnchanged
    ? previousInScope!.structuralRowOrderById
    : new Map(structuralRowIds.map((rowId, index) => [rowId, index]))
  const orderedStructuralRows = structuralOrderUnchanged && !contentChanged
    ? previousInScope!.orderedStructuralRows
    : structuralRowIds.map(rowId => rowById.get(rowId) as AnchoredRow)

  return {
    scopeKey,
    rows: rowsSnapshot,
    rowIds,
    entries,
    entryById,
    rowById,
    canonicalRowIndexById,
    orderedStructuralRows,
    structuralRowIds,
    structuralRowOrderById,
    changedRowIds,
    removedRowIds,
    contentRevision: previousInScope
      ? previousInScope.contentRevision + (contentChanged ? 1 : 0)
      : 1,
    orderRevision: previousInScope
      ? previousInScope.orderRevision + (orderChanged ? 1 : 0)
      : 1,
  }
}
